import classNames from 'classnames'
import styles from './CrossFade.module.css'
import { createPortal } from 'react-dom'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
type AcceptableChildren = string | number | boolean | null | undefined
async function tick({ frames }: { frames: number[] }) {
  return new Promise((resolve) => frames.push(window.requestAnimationFrame(resolve)))
}

async function wait(noOfTicks: number, frames: number[]) {
  const promiseFn = new Array(noOfTicks).fill(0).map((_, __) => () => tick({ frames }))
  for await (const promise of promiseFn) {
    await promise()
  }
}

function CrossFade({ children, className }: { children?: AcceptableChildren; className?: string | undefined }) {
  const prevChildrenRef = useRef<React.ReactNode>(null)
  const childrenRef = useRef<React.ReactNode>(null)
  const [index, setIndex] = useState<0 | 1>(0)
  const indexRef = useRef<0 | 1>(index)
  indexRef.current = index
  const ref0 = useRef<HTMLDivElement>(null)
  const ref1 = useRef<HTMLDivElement>(null)
  const refs = useRef([ref0, ref1] as const)
  const containerRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)

  const handleChildrenChange = useCallback(async (children: AcceptableChildren, frames: number[]) => {
    if (children === prevChildrenRef.current) return
    prevChildrenRef.current = children
    childrenRef.current = children
    const i = indexRef.current
    const j = i === 0 ? 1 : 0
    setIndex(j)
    await wait(4, frames)
    containerRef.current!.style.maxWidth = `${refs.current[j].current!.clientWidth}px`
    containerRef.current!.style.minWidth = `${refs.current[j].current!.clientWidth}px`
  }, [])

  useEffect(() => {
    setMounted(true)
    const frames: number[] = []
    void handleChildrenChange(children, frames).then().catch()
    return () => {
      frames.forEach((frame) => cancelAnimationFrame(frame))
      prevChildrenRef.current = children
    }
  }, [children, handleChildrenChange])

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={classNames(styles.crossFade, className, { [styles.hidden!]: index !== 0 })} ref={ref0} />
      <div className={classNames(styles.crossFade, className, { [styles.hidden!]: index !== 1 })} ref={ref1} />
      {mounted && createPortal(childrenRef.current ?? null, refs.current[index].current!)}
      {mounted && createPortal(prevChildrenRef.current ?? null, refs.current[index === 0 ? 1 : 0].current!)}
    </div>
  )
}

export default memo(CrossFade)
